/**
 * Barre de filtres commune à la table et au kanban.
 *
 * Les deux vues montrent le même ensemble de demandes : un filtre posé sur
 * l'une doit valoir pour l'autre, sinon basculer d'une vue à l'autre semble
 * perdre des demandes. L'état vit donc dans l'écran, ce composant ne fait que
 * l'afficher et le rendre modifiable.
 */
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import { LIBELLES_STATUT_LEAD, STATUTS_LEAD } from "./LeadsDonnees";

/** `null` en période signifie « depuis toujours ». */
export type Filtres = {
  statut: string;
  commune: string;
  jours: number | null;
};

export const FILTRES_VIDES: Filtres = { statut: "", commune: "", jours: null };

const PERIODES: { valeur: number | null; libelle: string }[] = [
  { valeur: 7, libelle: "7 derniers jours" },
  { valeur: 30, libelle: "30 derniers jours" },
  { valeur: 90, libelle: "3 derniers mois" },
  { valeur: 365, libelle: "12 derniers mois" },
  { valeur: null, libelle: "Depuis le début" },
];

export function estFiltre(filtres: Filtres): boolean {
  return filtres.statut !== "" || filtres.commune !== "" || filtres.jours !== null;
}

type Proprietes = {
  filtres: Filtres;
  surChangement: (filtres: Filtres) => void;
  /**
   * Le kanban range déjà les demandes par statut : lui ajouter un filtre de
   * statut reviendrait à vider quatre colonnes sur cinq pour montrer ce que la
   * cinquième affichait déjà.
   */
  avecStatut?: boolean;
};

export function LeadsFiltres({ filtres, surChangement, avecStatut = true }: Proprietes) {
  return (
    <Box
      sx={{
        display: "grid",
        gap: 2,
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "2fr 1fr 1fr auto" },
        alignItems: "center",
      }}
    >
      <TextField
        label="Commune"
        size="small"
        value={filtres.commune}
        onChange={(evenement) => surChangement({ ...filtres, commune: evenement.target.value })}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          },
        }}
      />

      {avecStatut && (
        <TextField
          select
          label="Statut"
          size="small"
          value={filtres.statut}
          onChange={(evenement) => surChangement({ ...filtres, statut: evenement.target.value })}
        >
          <MenuItem value="">Tous les statuts</MenuItem>
          {STATUTS_LEAD.map((statut) => (
            <MenuItem key={statut} value={statut}>
              {LIBELLES_STATUT_LEAD[statut]}
            </MenuItem>
          ))}
        </TextField>
      )}

      <TextField
        select
        label="Période"
        size="small"
        value={filtres.jours === null ? "tout" : String(filtres.jours)}
        onChange={(evenement) =>
          surChangement({
            ...filtres,
            jours: evenement.target.value === "tout" ? null : Number(evenement.target.value),
          })
        }
      >
        {PERIODES.map((periode) => (
          <MenuItem
            key={periode.valeur ?? "tout"}
            value={periode.valeur === null ? "tout" : String(periode.valeur)}
          >
            {periode.libelle}
          </MenuItem>
        ))}
      </TextField>

      <Button
        onClick={() => surChangement(FILTRES_VIDES)}
        disabled={!estFiltre(filtres)}
        sx={{ justifySelf: "start" }}
      >
        Tout afficher
      </Button>
    </Box>
  );
}
